import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { dpiaAPI, exportAPI } from '../../../services/api';
import { TasksCountBadge } from '../../../components/shared/TasksCountBadge';
import { useAuth } from '../../../contexts/AuthContext';
import { Shield, ExternalLink, Trash2, MoreHorizontal, FileDown, FileText } from 'lucide-react';
import { toast } from 'sonner';

export const DPIATable = ({ assessments, onRefresh, taskCounts = {} }) => {
  const navigate = useNavigate();
  const { isComplianceReadOnly } = useAuth();

  if (!assessments.length) return null;

  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Shield className="h-4 w-4 text-primary" />
          Data Protection Impact Assessments (DPIA)
        </h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Reference</TableHead>
              <TableHead>Organization</TableHead>
              <TableHead className="flex-1">Activity</TableHead>
              <TableHead>Initial Risk</TableHead>
              <TableHead>Residual Risk</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {assessments.map((dpia) => (
              <TableRow
                key={dpia.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => navigate(`/risk/dpia/${dpia.id}`)}
              >
                <TableCell className="font-mono text-sm">{dpia.dpia_reference || '-'}</TableCell>
                <TableCell>{dpia.entity_name || 'Unknown'}</TableCell>
                <TableCell className="font-medium flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span>{dpia.project_title || 'Untitled'}</span>
                    <TasksCountBadge counts={taskCounts[dpia.id]} />
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div
                      className="h-1.5 w-1.5 rounded-full flex-shrink-0"
                      style={{
                        backgroundColor:
                          dpia.initial_project_risk > 9 ? '#E24B4A' :
                          dpia.initial_project_risk > 6 ? '#854F0B' :
                          dpia.initial_project_risk > 3 ? '#BA7517' :
                          dpia.initial_project_risk != null ? '#639922' : '#BBBBBB'
                      }}
                    />
                    <span className={
                      dpia.initial_project_risk > 9 ? 'text-red-600 dark:text-red-400 font-medium' :
                      dpia.initial_project_risk > 6 ? 'text-orange-700 dark:text-orange-400' :
                      dpia.initial_project_risk > 3 ? 'text-amber-700 dark:text-amber-400' :
                      dpia.initial_project_risk != null ? 'text-green-700 dark:text-green-400' : 'text-muted-foreground'
                    }>
                      {dpia.initial_project_risk != null && !isNaN(dpia.initial_project_risk) ? Math.round(dpia.initial_project_risk * 10) / 10 : '-'}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div
                      className="h-1.5 w-1.5 rounded-full flex-shrink-0"
                      style={{
                        backgroundColor:
                          dpia.project_risk_after_controls > 9 ? '#E24B4A' :
                          dpia.project_risk_after_controls > 6 ? '#854F0B' :
                          dpia.project_risk_after_controls > 3 ? '#BA7517' :
                          dpia.project_risk_after_controls != null ? '#639922' : '#BBBBBB'
                      }}
                    />
                    <span className={
                      dpia.project_risk_after_controls > 9 ? 'text-red-600 dark:text-red-400 font-medium' :
                      dpia.project_risk_after_controls > 6 ? 'text-orange-700 dark:text-orange-400' :
                      dpia.project_risk_after_controls > 3 ? 'text-amber-700 dark:text-amber-400' :
                      dpia.project_risk_after_controls != null ? 'text-green-700 dark:text-green-400' : 'text-muted-foreground'
                    }>
                      {dpia.project_risk_after_controls != null && !isNaN(dpia.project_risk_after_controls) ? Math.round(dpia.project_risk_after_controls * 10) / 10 : '-'}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={
                    dpia.status === 'approved' ? 'success' :
                    dpia.status === 'rejected' ? 'destructive' :
                    dpia.status === 'pending_review' ? 'warning' : 'secondary'
                  }>
                    {dpia.status?.replace('_', ' ')}
                  </Badge>
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {/* Spec §DPIA table — Actions: Open, Export DOCX, Export PDF, Delete */}
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/risk/dpia/${dpia.id}`); }}>
                        <ExternalLink className="h-4 w-4 mr-2" />Open
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const res = await exportAPI.dpiaDocx(dpia.id);
                          const url = window.URL.createObjectURL(new Blob([res.data]));
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = res.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '')
                            || `DPIA_${dpia.dpia_reference || dpia.id}.docx`;
                          document.body.appendChild(a); a.click(); a.remove();
                          window.URL.revokeObjectURL(url);
                          toast.success('DOCX exported');
                        } catch { toast.error('Export failed'); }
                      }} data-testid={`export-dpia-docx-${dpia.id}`}>
                        <FileDown className="h-4 w-4 mr-2" />Export DOCX
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => {
                        e.stopPropagation();
                        // Spec requires Export PDF. No backend PDF generator exists today;
                        // we surface the menu item (spec compliance) and flag it.
                        toast.info('PDF export is coming soon — please use Export DOCX for now.');
                      }} data-testid={`export-dpia-pdf-${dpia.id}`}>
                        <FileText className="h-4 w-4 mr-2" />Export PDF
                      </DropdownMenuItem>
                      {!isComplianceReadOnly && (
                        <DropdownMenuItem
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (window.confirm('Delete this DPIA?')) {
                              try {
                                await dpiaAPI.delete(dpia.id);
                                toast.success('DPIA deleted');
                                onRefresh();
                              } catch { toast.error('Failed to delete'); }
                            }
                          }}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />Delete
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
